import { Chain } from "./chain";
import { Pt, pt, ptEquals } from "./pt";

interface Entry<V> {
  key: Pt;
  value: V;
}

class PointMap<V> {
  xs: Map<number, Map<number, V>>;
  constructor() {
    this.xs = new Map();
  }

  set(pt: Pt, v: V) {
    let ys = this.xs.get(pt.x);
    if (!ys) {
      ys = new Map();
      this.xs.set(pt.x, ys);
    }
    ys.set(pt.y, v);
  }

  get(pt: Pt): V {
    const ys = this.xs.get(pt.x);
    if (ys) {
      return ys.get(pt.y);
    }
  }

  entries(): Entry<V>[] {
    return Array.from(this.xs.entries()).flatMap((kv1) => {
      let x = kv1[0];
      let ys = kv1[1];
      return Array.from(ys.entries()).map((kv2) => {
        let y = kv2[0];
        let v = kv2[1];
        return { key: pt(x, y), value: v };
      });
    });
  }

  keys(): Pt[] {
    return this.entries().map((e) => e.key);
  }
}

class Graph<T> {
  private edges: PointMap<PointMap<T>>;

  constructor() {
    this.edges = new PointMap();
  }

  add(start: Pt, end: Pt, label: T) {
    let ends = this.edges.get(start);
    if (!ends) {
      ends = new PointMap();
      this.edges.set(start, ends);
    }
    ends.set(end, label);
  }

  findCycles(): T[][] {
    const j = new Johnson();
    return j.findCyles(this);
  }

  entries(): Entry<PointMap<T>>[] {
    return this.edges.entries();
  }

  dests(node: Pt): Entry<T>[] {
    const pm = this.edges.get(node);
    if (pm) return pm.entries();
    else return [];
  }

  destPts(node: Pt): Pt[] {
    return this.dests(node).map((e) => e.key);
  }

  dump() {
    for (let { key, value } of this.entries()) {
      console.log(`(${key.x},${key.y}):`);
      for (let { key: k, value: v } of value.entries()) {
        console.log(`   (${k.x}, ${k.y})`);
      }
    }
  }
}

class Johnson {
  private blocked: PointMap<boolean>;
  private b: PointMap<PointMap<boolean>>;

  constructor() {
    this.blocked = new PointMap();
    this.b = new PointMap();
  }

  findCyles<T>(graph: Graph<T>): T[][] {
    const cycles = [];
    for (let src of graph.entries()) {
      for (let dst of src.value.entries()) {
        let maybeCircuit = this.cycle(dst.key, [dst.value], src.key, graph);
        if (maybeCircuit) cycles.push(maybeCircuit);
      }
    }
    return cycles;
  }

  private unblock(u: Pt) {
    this.blocked.set(u, false);
    const bu = this.b.get(u);
    if (bu) {
      bu.keys().forEach((w) => {
        bu.set(w, false);
        if (this.blocked.get(w)) {
          this.unblock(w);
        }
      });
    }
  }

  private cycle<T>(node: Pt, stack: T[], startNode: Pt, graph: Graph<T>): T[] {
    this.blocked.set(node, true);
    for (let { key, value } of graph.dests(node)) {
      let newStack = stack.concat(value);
      if (ptEquals(key, startNode)) {
        return newStack;
      } else if (!this.blocked.get(key)) {
        let maybeCircuit = this.cycle(key, newStack, startNode, graph);
        if (maybeCircuit) return maybeCircuit;
      }
    }

    graph.destPts(node).forEach((w) => {
      var entry = this.b.get(w);

      if (!entry) {
        entry = new PointMap();
        this.b.set(w, entry);
      }

      entry.set(node, true);
    });
  }
}

function makeChains(arcs: Chain[]): Chain[] {
  const g = new Graph<Chain>();
  arcs.forEach((a) => g.add(a.start, a.end, a));
  const consumed = new Set<Chain>();
  const result: Chain[] = [];
  for (let cycle of g.findCycles()) {
    if (cycle.every((c) => !consumed.has(c))) {
      result.push(new Chain(cycle.flatMap((c) => c.segments)));
      cycle.forEach((c) => consumed.add(c));
    }
  }
  return result;
}

export { makeChains };
